"use client";
import React, { useState, useEffect, useRef } from "react";
import Link from "next/link";
import Image from "next/image";
import { useRouter } from "next/navigation";
import Banner from "@/components/Blogs/Banner";
import BlogCards from "@/components/Blogs/BlogCards";
import Breadcrumbs from "@/components/Breadcrumbs";
import Pagination from "@/components/Pagination";
import Category from "@/components/Blogs/Category";
import SearchIcon from "@/assets/icons/search-icon.png";
import { getCategories, GetAllBlog } from "@/helpers/BlogApis";

// Skeleton loader component
const BlogCardSkeleton = () => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((i) => (
      <div key={i} className="animate-pulse">
        <div className="bg-gray-200 h-48 rounded-t-lg"></div>
        <div className="p-4 space-y-3">
          <div className="h-4 bg-gray-200 rounded w-3/4"></div>
          <div className="h-3 bg-gray-200 rounded w-full"></div>
          <div className="h-3 bg-gray-200 rounded w-5/6"></div>
        </div>
      </div>
    ))}
  </div>
);

const CategoryClient = ({ category, blogsData }) => {
  const router = useRouter();
  const isInitialMount = useRef(true);

  const categorySlug = category.slug || category.name.toLowerCase().replace(/\s+/g, "-");

  const [currentPage, setCurrentPage] = useState(1);
  const [blogs, setBlogs] = useState(blogsData?.data || []);
  const [totalPages, setTotalPages] = useState(blogsData?.meta?.pagination?.pageCount || 1);
  const [isLoadingBlogs, setIsLoadingBlogs] = useState(false);
  
  const [searchQuery, setSearchQuery] = useState("");
  const [categoriesList, setCategoriesList] = useState([
    { name: "All Blogs", id: null, description: "View all blog posts" },
  ]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const data = await getCategories();
        if (data?.data) {
          setCategoriesList([
            { name: "All Blogs", id: null, description: "View all blog posts" },
            ...data.data,
          ]);
        }
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    const fetchBlogs = async () => {
      setIsLoadingBlogs(true);
      try {
        const data = await GetAllBlog(null, currentPage, 12, searchQuery, null, categorySlug);
        if (data) {
          setBlogs(data.data || []);
          setTotalPages(data.meta?.pagination?.pageCount || 1);
        }
      } catch (error) {
        console.error("Error fetching blogs:", error);
      } finally {
        setIsLoadingBlogs(false);
      }
    };

    fetchBlogs();
  }, [searchQuery, currentPage, categorySlug]);

  const handleCategoryClick = (cat) => {
    if (cat.name === "All Blogs") {
      router.push("/blog");
    } else {
      const slug = cat.slug || cat.name.toLowerCase().replace(/\s+/g, "-");
      router.push(`/blog/${slug}`);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <>
      <Banner title="Skill-Based Education, Career Guidance & Industry Insights | MSU Blog" />
      <Breadcrumbs
        items={[
          { label: "Blog", href: "/blog" },
          { label: category.name, href: `/blog/${categorySlug}` },
        ]}
      />

      <section className="max-w-(--max-page-width) mx-auto px-(--container-padding) w-full my-6">
        {/* Mobile Search */}
        <div className="flex relative justify-center items-center pb-2 sm:hidden mb-4">
          <div className="flex items-center border border-slate-300 rounded overflow-hidden w-full">
            <input
              className="placeholder:text-slate-400 bg-white py-2 px-4 focus:outline-none w-full"
              placeholder="Search for anything..."
              type="text"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
            />
            <button
              onClick={() => setCurrentPage(1)}
              className="bg-[#007BAC] hover:bg-[#005D82] text-white px-4 py-3 flex items-center justify-center cursor-pointer h-full"
            >
              <Image
                src={SearchIcon}
                width={20}
                height={20}
                alt="Search icon"
                className="object-contain filter brightness-0 invert"
              />
            </button>
          </div>
        </div>
        {/* Desktop Search */}
        <div className="scrollbar-hidden flex items-center overflow-auto justify-between border-t-2 border-b-2 border-[#6167773d] pt-4">
          <Category
            categories={categoriesList}
            handleCategory={handleCategoryClick}
            activeCategory={category.name}
          />
          <div className="flex items-center pb-2 mr-4 hidden sm:flex -mt-[10px]">
            <div className="flex items-center border border-slate-300 overflow-hidden">
              <input
                className="bg-white py-[9px] px-4 focus:outline-none w-64 placeholder:italic placeholder:text-slate-400"
                placeholder="Search for anything..."
                type="text"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
              />
              <button
                onClick={() => setCurrentPage(1)}
                className="bg-[#007bac] hover:bg-[#005D82] text-white px-4 py-[10px] flex items-center justify-center cursor-pointer h-full"
              >
                Search
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-(--max-page-width) mx-auto px-(--container-padding) w-full my-6">
          <div className="flex justify-between items-center w-full mb-6">
            <p className="text-3xl font-bold text-gray-900">
              {category.name}
            </p>
          </div>

          {/* Subcategories */}
          {category.subcategories && category.subcategories.length > 0 && (
            <div className="mb-8 bg-white rounded-lg">
              <div className="flex flex-wrap gap-3">
                <Link
                  href={`/blog/${categorySlug}`}
                  className="
                    flex-shrink-0
                    capitalize
                    text-[14px] sm:text-[15px] md:text-[16px]
                    font-medium sm:font-bold
                    px-4 py-2
                    border border-transparent
                    transition-all
                    bg-[#FFA201] text-black
                  "
                >
                  All
                </Link>
                {category.subcategories.map((subcat) => (
                  <Link
                    key={subcat.id}
                    href={`/blog/${categorySlug}/${subcat.slug}`}
                    className="
                      flex-shrink-0
                      capitalize
                      text-[14px] sm:text-[15px] md:text-[16px]
                      font-medium sm:font-bold
                      px-4 py-2
                      border border-[#00000033]
                      transition-all
                      text-black hover:bg-[#FFA201] hover:border-transparent
                    "
                  >
                    {subcat.name}
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Blog Cards */}
          {isLoadingBlogs ? (
            <BlogCardSkeleton />
          ) : blogs.length > 0 ? (
            <>
              <BlogCards blogs={blogs} />

              {totalPages > 1 && (
                <div className="mt-12">
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    setPage={handlePageChange}
                    buildHref={(page) => page === 1 ? `/blog/${categorySlug}` : `/blog/${categorySlug}/?page=${page}
                    `}
                  />
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-12 bg-white rounded-lg">
              <p className="text-gray-600 text-lg">
                No articles found in this category.
              </p>
              <Link
                href="/blog"
                className="mt-4 inline-block text-blue-600 hover:text-blue-800"
              >
                Back to all blogs
              </Link>
            </div>
          )}
      </section>
    </>
  );
};

export default CategoryClient;

